import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { ClipPublic } from '@proref/types';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Icon } from '../shared/Icons';
import { Input } from '../shared/Input';
import { Button } from '../shared/Button';
import { Spinner } from '../shared/Spinner';
import { Skeleton } from '../shared/Skeleton';
import { apiErrorMessage } from '../../lib/api';
import { notifyError, notifySuccess } from '../../lib/toast';
import { timeAgo } from '../../lib/format';
import { tagSoftStyle } from '../../lib/tag-colors';
import { listClips } from '../../services/clips.service';
import { useAddExamClipQuestions } from '../../hooks/use-exams';

// Whole-second mm:ss for the clip's duration pill (duration_sec is numeric, may be null pre-transcode).
function mmss(sec: number | null): string {
  if (sec === null) return '—';
  const s = Math.round(sec);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

// One selectable card in the picker — deliberately the SAME thumbnail-on-top card shape as the
// referee-facing Library grid (RefereeClipsPage's GridCard), on request: an admin picking clips
// for an exam should recognise it as "the library", not a distinct, plainer list. Differs only in
// the interaction (toggles a checkbox instead of navigating to the player) and the selection
// affordance (a checkbox glyph over the thumbnail instead of an active border). A clip already on
// the exam renders checked + disabled so it can't be double-added (the server skips dupes anyway,
// but showing it avoids a confusing no-op).
function ClipCard({
  clip,
  selected,
  alreadyOn,
  onToggle,
}: {
  clip: ClipPublic;
  selected: boolean;
  alreadyOn: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const checked = selected || alreadyOn;
  return (
    <button
      type="button"
      onClick={alreadyOn ? undefined : onToggle}
      aria-pressed={checked}
      disabled={alreadyOn}
      className={[
        'group flex flex-col overflow-hidden rounded border bg-surface text-left transition-colors',
        alreadyOn
          ? 'cursor-default border-line opacity-60'
          : checked
            ? 'border-accent shadow-1'
            : 'border-line-2 hover:border-line-3 hover:bg-surface-2',
      ].join(' ')}
    >
      <div className="relative aspect-video w-full overflow-hidden bg-bg-2">
        {clip.poster_url ? (
          <img src={clip.poster_url} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="grid h-full w-full place-items-center text-[11px] text-muted">{t('common.noPoster')}</div>
        )}
        {/* Checkbox glyph, top-left over the thumbnail — the multi-select equivalent of the
            Library grid's active border, since a border alone doesn't read as "selectable" here. */}
        <span
          className={
            'absolute left-1.5 top-1.5 grid h-[20px] w-[20px] flex-none place-items-center rounded border ' +
            (checked ? 'border-accent bg-accent text-accent-ink' : 'border-line-3 bg-surface/90')
          }
        >
          {checked && <Icon.check className="h-3 w-3" />}
        </span>
        <span className="absolute bottom-1.5 right-1.5 rounded-sm bg-ink/85 px-1.5 py-0.5 text-[11px] font-medium text-bg">
          {mmss(clip.duration_sec)}
        </span>
        {alreadyOn && (
          <span className="absolute right-1.5 top-1.5 rounded-sm bg-ink/85 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-bg">
            {t('admin.exams.clipPicker.onExam')}
          </span>
        )}
      </div>
      <div className="min-w-0 flex-1 p-2.5">
        <div className="line-clamp-2 text-[13px] font-medium text-ink">{clip.title}</div>
        <div className="mt-1 text-[11px] text-muted">
          {clip.rule_article ? t('common.ruleLabel', { number: clip.rule_article.article_number }) : t('common.untagged')}
          {' · '}
          {timeAgo(clip.uploaded_at)}
        </div>
        {clip.tags && clip.tags.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {clip.tags.map((t) => (
              <span
                key={t.id}
                style={tagSoftStyle(t.color)}
                className="rounded-full px-2 py-0.5 text-[10px] font-medium"
              >
                {t.label}
              </span>
            ))}
          </div>
        )}
      </div>
    </button>
  );
}

interface ClipPickerDialogProps {
  examId: string;
  federationId: string;
  sportId: string;
  // An Official (platform) exam draws from OFFICIAL clips (federation_id NULL), not a federation's.
  official: boolean;
  // Clip ids already on the exam — pre-checked + disabled so they can't be re-added.
  existingClipIds: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Multi-select clip picker — the "build an exam out of selected videos" flow. Lists the exam's
// federation + sport library as a searchable card grid — the same GridCard shape as the
// referee-facing Library grid (RefereeClipsPage), on request, so authoring an exam from clips
// looks like browsing the library rather than a separate, plainer picker. "Add N clips" posts
// the selection all in one batch call. Self-contained: owns selection, the mutation, and its own toasts.
export function ClipPickerDialog({
  examId,
  federationId,
  sportId,
  official,
  existingClipIds,
  open,
  onOpenChange,
}: ClipPickerDialogProps) {
  const { t } = useTranslation();
  const add = useAddExamClipQuestions(examId);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const clipsQuery = useQuery({
    queryKey: ['exam-clip-picker', official ? 'official' : federationId, sportId],
    queryFn: () => listClips(official ? { official: true, sportId } : { federationId, sportId }),
    staleTime: 30_000,
    enabled: open, // don't fetch the library until the picker is actually opened
  });

  const existing = useMemo(() => new Set(existingClipIds), [existingClipIds]);

  // Case-insensitive title filter; the already-on clips still show (disabled) for context.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const clips = clipsQuery.data ?? [];
    if (!q) return clips;
    return clips.filter((c) => c.title.toLowerCase().includes(q));
  }, [clipsQuery.data, query]);

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Reset selection + search whenever the dialog closes, so re-opening starts clean.
  function handleOpenChange(next: boolean) {
    if (!next) {
      setSelected(new Set());
      setQuery('');
    }
    onOpenChange(next);
  }

  async function confirm() {
    const clip_ids = [...selected];
    if (clip_ids.length === 0) return;
    try {
      const added = await add.mutateAsync({ clip_ids });
      notifySuccess(t('admin.exams.clipPicker.addedToast', { count: added.length }));
      handleOpenChange(false);
    } catch (err) {
      notifyError(err, t('admin.exams.clipPicker.addErrorToast'));
    }
  }

  const count = selected.size;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {/* Wider than a typical form dialog — a card grid needs the room a list didn't. */}
      <DialogContent className="flex max-h-[85vh] w-[min(95vw,60rem)] max-w-none flex-col">
        <DialogHeader>
          <DialogTitle>{t('admin.exams.clipPicker.title')}</DialogTitle>
          <DialogDescription>
            {t('admin.exams.clipPicker.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="relative mt-1">
          <Icon.search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('admin.clips.list.searchPlaceholder')}
            className="pl-9"
            aria-label={t('admin.exams.clipPicker.searchAria')}
          />
        </div>

        {/* Scrollable grid — the only part that grows, so the header + footer stay pinned. Same
            card-grid shape as the referee-facing Library grid, requested so this reads as "the
            library" rather than a separate, plainer picker. */}
        <div className="mt-3 min-h-0 flex-1 overflow-y-auto scroll-thin pr-1">
          {clipsQuery.isLoading ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="aspect-video w-full" />
              ))}
            </div>
          ) : clipsQuery.isError ? (
            <div className="rounded border border-danger/40 bg-danger/10 px-4 py-3 text-[13px] text-danger">
              {apiErrorMessage(clipsQuery.error, t('admin.clips.list.couldNotLoad'))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="grid place-items-center rounded border border-dashed border-line-2 bg-bg-2 px-6 py-10 text-center">
              <p className="text-sm text-muted">
                {(clipsQuery.data ?? []).length === 0
                  ? t('admin.exams.clipPicker.noClipsYet')
                  : t('admin.exams.clipPicker.noMatches')}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {filtered.map((clip) => (
                <ClipCard
                  key={clip.id}
                  clip={clip}
                  selected={selected.has(clip.id)}
                  alreadyOn={existing.has(clip.id)}
                  onToggle={() => toggle(clip.id)}
                />
              ))}
            </div>
          )}
        </div>

        <div className="mt-3 flex items-center justify-between border-t border-line pt-3">
          <span className="text-[12.5px] text-muted">
            {count === 0 ? t('admin.exams.clipPicker.noneSelected') : t('admin.exams.clipPicker.selectedCount', { count })}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => handleOpenChange(false)} disabled={add.isPending}>
              {t('common.cancel')}
            </Button>
            <Button variant="accent" size="sm" onClick={confirm} disabled={count === 0 || add.isPending}>
              {add.isPending ? (
                <Spinner className="w-4 h-4" />
              ) : count === 0 ? (
                t('admin.exams.clipPicker.addButtonEmpty')
              ) : (
                t('admin.exams.clipPicker.addButton', { count })
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
